/**
 * Hitting things.
 *
 * One table, three bands, and every collision in the game reads it — a planet
 * today, a station deck and another ship's hull later. Keeping the speed-to-
 * consequence mapping in one place is the whole point: what a hundred units a
 * second does to you should not depend on what you hit it against.
 *
 * ── The bands ───────────────────────────────────────────────────────────────
 *
 * Deliberately steps rather than a curve. A curve would make every collision a
 * slightly different amount of bad and none of them memorable; steps make speed
 * a decision. There is a speed below which you bounce, one above which you are
 * hurt, and one above which you are gone, and a pilot can learn all three.
 *
 * The pushback runs the other way to the damage, which is the part that reads
 * as strange until you fly it: arrive slowly and the body shoves you off, arrive
 * quickly and it barely deflects you at all. That is what makes speed dangerous
 * rather than merely expensive — at a crawl the surface saves you from yourself,
 * and at four hundred it does nothing but stop you.
 */
/** Past this, nothing survives. */
export const IMPACT_FATAL_SPEED = 350;
/** Past this, it is a real accident rather than a scrape. */
export const IMPACT_SEVERE_SPEED = 120;
/**
 * Damage across the survivable range, before a line's strength is taken off it.
 *
 * Ramps rather than stepping with the bands. The bands decide what *kind* of
 * accident it was — whether you keep your momentum, how hard you are shoved off
 * — but the number that comes off the structure should follow the speed, or a
 * scrape at a hundred and nineteen and one at a hundred and twenty-one would
 * differ by more than the two units between them.
 *
 * Ten is the floor because touching a planet at any speed should cost
 * something. Eighty at the fatal threshold is most of a standard line, which is
 * the sentence these numbers are meant to make: a survivable collision is one
 * you feel for the rest of the trip.
 */
const DAMAGE_AT_REST = 10;
const DAMAGE_AT_FATAL = 80;
/** How hard a surface shoves back, in world units per second. */
const GLANCING_PUSHBACK = 60;
const SEVERE_PUSHBACK = 20;
/**
 * What happens to a hull arriving at a surface at this speed.
 *
 * Speed is the *closing* speed — relative to whatever was hit. Near anything
 * large enough to hit you are inside its mass lock and already sharing its
 * motion, so in practice this is simply how fast you were going.
 */
export function impactAt(speed) {
    const closing = Math.abs(speed);
    if (closing > IMPACT_FATAL_SPEED) {
        return { severity: 'destroyed', damage: Infinity, pushback: 0, killsMomentum: true };
    }
    if (closing > IMPACT_SEVERE_SPEED) {
        return {
            severity: 'severe',
            damage: impactDamage(closing),
            pushback: SEVERE_PUSHBACK,
            killsMomentum: true,
        };
    }
    return {
        severity: 'glancing',
        damage: impactDamage(closing),
        pushback: GLANCING_PUSHBACK,
        killsMomentum: false,
    };
}
/**
 * How much of the closing speed a station gives back.
 *
 * Half, and it is a deflection rather than a shove — which is the difference
 * between a station and a planet, and the reason this is not the table above.
 */
export const STATION_RESTITUTION = 0.5;
/**
 * The least a station will push back with, in units per second.
 *
 * Half of nothing is nothing, and a hull that touches a deck at a crawl has to
 * end up outside it — so the deflection has a floor. Small on purpose: this is
 * the ship declining to be inside a wall, not the station shoving it off.
 */
export const STATION_MIN_PUSHBACK = 2.5;
/**
 * The least a contact costs, in hull points.
 *
 * Scaling damage with the square of speed makes a slow enough touch free, and
 * free is the wrong answer: bouncing off a station should always be a bad idea,
 * just not a fatal one. This is set so that a starter hull survives around two
 * hundred of them — far more than any docking will ever take, and still a number
 * you would not want to find out.
 */
export const STATION_MIN_DAMAGE = 0.5;
/**
 * What happens to a hull arriving at a *station* at this speed.
 *
 * A planet shoves. Its pushback is a flat number of units a second that ignores
 * how fast you arrived, on the reasoning that a surface saves you from yourself
 * at a crawl — and against a body kilometres across, being firmly set back on
 * your way is right.
 *
 * A deck is not kilometres across. Brushing one at walking pace and being fired
 * off at sixty units a second reads as the station hitting *you*, and it makes
 * the last few metres of an approach — the part that should be delicate —
 * something you get flung out of.
 *
 * So a station deflects instead: you leave along the surface normal with half
 * the speed you came in at, and a fast arrival rebounds harder than a slow one
 * rather than the other way about. Anything you were doing *along* the surface
 * you keep, so a shallow scrape down a deck stays a scrape.
 *
 * Above manoeuvring speed the damage is the same table as everything else in the
 * game: hitting a deck at two hundred costs what hitting a moon at two hundred
 * costs. Below it, see `gentleness` — a station is the one surface you touch on
 * purpose, and the table's flat floor made parking lethal.
 *
 * Above the fatal speed nothing is deflected, because nothing is left to
 * deflect.
 */
export function stationImpactAt(speed) {
    const impact = impactAt(speed);
    if (impact.severity === 'destroyed')
        return impact;
    return {
        ...impact,
        damage: Math.max(STATION_MIN_DAMAGE, impact.damage * gentleness(speed)),
        pushback: Math.max(STATION_MIN_PUSHBACK, Math.abs(speed) * STATION_RESTITUTION),
        // Kept even for a severe hit. The band decides what the collision *cost*;
        // against a deck it should not also decide that you stop dead, or a heavy
        // landing pins the ship to the surface it just bounced off.
        killsMomentum: false,
    };
}
/** Below this, touching a station is manoeuvring rather than crashing. */
export const STATION_GENTLE_SPEED = 15;
/**
 * How much of a collision's damage a station actually charges for, 0..1.
 *
 * The damage table has a floor — ten points for touching anything at all — and
 * against a planet that is right, because you do not brush a planet. A station
 * you brush constantly: lining up on a pad on precision thrusters is a sequence
 * of small contacts, and a flat ten a time killed pilots for parking badly.
 *
 * So below the speed you manoeuvre at, the charge falls away with the *square*
 * of how fast you were going. Squared rather than linear because that is what
 * makes the bottom of the range genuinely free: at a third of the threshold a
 * linear scale still bills a third, and a ninth is what it should feel like.
 *
 * Full price at the threshold and above, so nothing is discounted that was not
 * a manoeuvre.
 */
export function gentleness(speed) {
    const closing = Math.abs(speed);
    if (closing >= STATION_GENTLE_SPEED)
        return 1;
    const fraction = closing / STATION_GENTLE_SPEED;
    return fraction * fraction;
}
/**
 * Raw damage for a collision at this speed.
 *
 * Linear between the two ends. Exported because it is the number, not the
 * band, that other kinds of damage will want to be consistent with — weapons
 * fire and a deck strike should cost a hull the same for the same energy.
 */
export function impactDamage(speed) {
    const fraction = Math.min(1, Math.abs(speed) / IMPACT_FATAL_SPEED);
    return DAMAGE_AT_REST + (DAMAGE_AT_FATAL - DAMAGE_AT_REST) * fraction;
}
//# sourceMappingURL=impact.js.map